import React, { useState } from 'react';
import useSWR from 'swr';
import { Alert, Avatar, Button, Chip, Grid, IconButton } from '@mui/material';
import Typography from '@mui/material/Typography';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import PlaceIcon from '@mui/icons-material/Place';
import { Link as RouterLink, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuthInfo } from '@propelauth/react';
import { useTranslation } from 'react-i18next';
import { Language, OrgRole, OrgUserOut, UserCredentialsOut, UserPreferencesService, UserService } from '../api';
import { UseAuthPermissions } from '../session/UseAuthPermissions';
import { useGetAccountAuthenticated } from '../session/swr/useGetAccountAuthenticated';
import { mb } from '../common/theme/mbTokens';
import { useLanguage } from '../common/GeneralUtils';
import { getActiveLocations, getLocationName, isMultiLocation } from '../common/filter/locationGrouping';
import RotatingLoader from '../common/ui/RotatingLoader';
import { TabsPage } from '../common/components/TabsPage';
import { UserAvailabilityCalendar } from '../userSettings/userAvailabilityCalendar/UserAvailabilityCalendar';
import { PermanentUserAvailability } from '../userSettings/permenantUserAvailability/PermanentUserAvailability';
import { Qualifications } from '../userSettings/qualifications/Qualifications';
import { UserSyncSettings } from '../userSettings/UserSyncSettings';
import { UserDetailsTab } from './UserDetailsTab';
import { CredentialsDialog } from './CredentialsDialog';
import { formatBlockedBy, formatLastActive, userFullName, userInitials } from './userManagementUtils';

const USERS_PATH = '/dashboard/users';

enum UserPageTabs {
  USER_DETAILS = 'userdetails',
  CALENDAR_AVAILABILITY = 'calendaravailability',
  PERMANENT_AVAILABILITY = 'permanentavailability',
  QUALIFICATIONS = 'qualifications',
  USER_SYNC_SETTINGS = 'usersyncettings',
}

const UserPageContent = () => {
  const { t } = useTranslation();
  const language = useLanguage();
  const navigate = useNavigate();
  const { userId } = useParams();
  const { account, updateLocalAccount } = useGetAccountAuthenticated({});
  const [credentials, setCredentials] = useState<UserCredentialsOut | undefined>(undefined);

  const {
    data: users,
    error: usersError,
    mutate: refetchUsers,
  } = useSWR({ opId: 'getOrgUsers' }, () => UserService.getOrgUsers());
  const isUserInOrg = !!users?.some((orgUser) => orgUser.user_id === userId);
  const {
    data: userPreferences,
    isLoading: isLoadingUserPreferences,
    mutate: refetchUserPrefs,
  } = useSWR(
    isUserInOrg ? { opId: 'getUserPreferences', userId } : null,
    () => UserPreferencesService.getUserPreferences(userId),
    { refreshInterval: 20000 },
  );

  if (usersError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" sx={{ textTransform: 'none' }} onClick={() => refetchUsers()}>
            {t('users.try_again')}
          </Button>
        }>
        {t('users.load_error')}
      </Alert>
    );
  }

  if (!users) {
    return (
      <Grid container justifyContent="center" alignItems="center" minHeight="200px">
        <RotatingLoader />
      </Grid>
    );
  }

  const user = users.find((orgUser) => orgUser.user_id === userId);

  if (!user) {
    return (
      <Grid container flexDirection="column" alignItems="flex-start" gap={2}>
        <Alert severity="warning">{t('users.user_not_found')}</Alert>
        <Button component={RouterLink} to={USERS_PATH} sx={{ textTransform: 'none' }}>
          {t('users.back')}
        </Button>
      </Grid>
    );
  }

  const BackIcon = language === Language.HE ? ArrowForward : ArrowBack;
  const isLastEnabledAdmin = users.filter((orgUser) => orgUser.enabled && orgUser.role === OrgRole.ADMIN).length <= 1;
  const blockedBy = formatBlockedBy(t, user);
  const shouldShowLocation = !!account && isMultiLocation(account);
  const userLocation = shouldShowLocation
    ? getActiveLocations(account).find((location) => location.id === user.location_id)
    : undefined;
  const userLocationLabel =
    shouldShowLocation && user.is_floating
      ? t('users.location_floating')
      : userLocation && getLocationName(userLocation, language);

  const handleChanged = (updatedUser: OrgUserOut) => {
    void refetchUsers(users.map((orgUser) => (orgUser.user_id === updatedUser.user_id ? updatedUser : orgUser)));
    void updateLocalAccount();
  };

  const tabContent = {
    [UserPageTabs.USER_DETAILS]: (
      <UserDetailsTab
        user={user}
        isLastEnabledAdmin={isLastEnabledAdmin}
        qualifiedOrderPageIds={
          userPreferences?.qualified_order_pages_settings?.map((setting) => setting.order_page_id) ?? []
        }
        onChanged={handleChanged}
        onCredentials={setCredentials}
      />
    ),
    [UserPageTabs.CALENDAR_AVAILABILITY]: (
      <UserAvailabilityCalendar
        userPreferences={userPreferences}
        isLoadingUserPreferences={isLoadingUserPreferences}
        refetchUserPrefs={refetchUserPrefs}
        selectedUserId={userId}
        fullUserName={userFullName(user)}
      />
    ),
    [UserPageTabs.PERMANENT_AVAILABILITY]: (
      <PermanentUserAvailability
        userPreferences={userPreferences}
        isLoadingUserPreferences={isLoadingUserPreferences}
        refetchUserPrefs={refetchUserPrefs}
        selectedUserId={userId}
      />
    ),
    [UserPageTabs.QUALIFICATIONS]: (
      <Qualifications
        userPreferences={userPreferences}
        isLoadingUserPreferences={isLoadingUserPreferences}
        refetchUserPrefs={refetchUserPrefs}
        selectedUserId={userId}
        onChangeLocation={() => {
          window.location.hash = `#${UserPageTabs.USER_DETAILS}`;
        }}
      />
    ),
    ...(user.is_current_user && {
      [UserPageTabs.USER_SYNC_SETTINGS]: (
        <UserSyncSettings
          userPreferences={userPreferences}
          isLoadingUserPreferences={isLoadingUserPreferences}
          refetchUserPrefs={refetchUserPrefs}
          selectedUserId={userId}
        />
      ),
    }),
  };

  return (
    <Grid
      container
      height="100%"
      overflow="hidden"
      flexDirection="column"
      flexWrap="nowrap"
      sx={{ gap: mb.layout.pageInset }}>
      <Grid container alignItems="center" flexWrap="nowrap" gap={1.5} sx={{ flexShrink: 0 }}>
        <IconButton aria-label={t('users.back')} onClick={() => navigate(USERS_PATH)}>
          <BackIcon />
        </IconButton>
        <Avatar variant="rounded" sx={{ width: 48, height: 48 }}>
          {userInitials(user)}
        </Avatar>
        <Grid container flexDirection="column" gap={0.5} sx={{ flex: 1, minWidth: 0 }}>
          <Grid container alignItems="center" gap={1}>
            <Typography variant="h5">{userFullName(user)}</Typography>
            <Chip size="small" label={t(`users.roles.${user.role}`)} />
            {user.enabled ? (
              <Chip size="small" color="success" label={t('users.status.active')} />
            ) : (
              <Chip size="small" label={t('users.status.blocked')} />
            )}
            {userLocationLabel && (
              <Chip
                size="small"
                variant="outlined"
                icon={<PlaceIcon sx={{ fontSize: 16 }} />}
                label={userLocationLabel}
              />
            )}
            {user.is_current_user && <Chip size="small" label={t('users.you')} />}
          </Grid>
          <Grid container alignItems="center" gap={1}>
            <Typography variant="caption" color="text.secondary" dir="ltr">
              {user.email}
            </Typography>
            {user.last_active_at ? (
              <Typography variant="caption" color="text.secondary">
                {t('users.drawer.last_active', { value: formatLastActive(user.last_active_at) })}
              </Typography>
            ) : (
              <Chip size="small" color="warning" label={t('users.never_logged_in')} />
            )}
            {!user.enabled && blockedBy && (
              <Typography variant="caption" color="text.secondary">
                {blockedBy}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Grid>
      <Grid container flexDirection="column" flexWrap="nowrap" sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <TabsPage tabContent={tabContent} />
      </Grid>
      {credentials && (
        <CredentialsDialog
          isOpen
          onClose={() => setCredentials(undefined)}
          user={user}
          credentials={credentials}
          isNewUser={false}
        />
      )}
    </Grid>
  );
};

export const UserPage = () => {
  const { loading } = useAuthInfo();
  const { isAdmin } = UseAuthPermissions();

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" minHeight="200px">
        <RotatingLoader />
      </Grid>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard/calendar" replace />;
  }

  return <UserPageContent />;
};
